import type { OpenAPIV3 } from 'openapi-types';

import { basicTypes, type SchemaFormat, type SchemaType } from '../../TypeMenu';

export type EnumValue = NonNullable<OpenAPIV3.SchemaObject['enum']>[number];

export type SupportedTypes = boolean | number | string;

export function isEnumTypeSupported(type: OpenAPIV3.SchemaObject['type']): boolean {
  return basicTypes.some(basicType => basicType.type === type);
}

export const typePlaceholders: Partial<Record<SchemaType, string>> = {
  string: 'owl',
  number: '10.5',
  integer: '10',
  boolean: 'true',
};

export const formatPlaceholders: Record<SchemaFormat, string> = {
  binary: '1100',
  byte: 'U3dhZ2dlciByb2Nrcw==',
  'date-time': '2025-01-01T00:00:00Z',
  date: '2025-01-01',
  double: '10.48',
  float: '10.4',
  int32: '10',
  int64: '10',
  password: 'password',
};

// Validation
function isNumber(value: string) {
  if (value.trim() === '') return false;
  const num = Number(value);
  return !Number.isNaN(num) && Number.isFinite(num);
}

function validateEnumTypeForString(value: string, type: SchemaType) {
  switch (type) {
    case 'boolean':
      return value.toLowerCase() === 'true' || value.toLowerCase() === 'false';
    case 'integer': {
      return isNumber(value) && Number.isInteger(Number(value));
    }
    case 'number': {
      return isNumber(value);
    }
    case 'string':
      return true;
    default:
      return false;
  }
}

export function validateEnumType(value: EnumValue, type: SchemaType) {
  if (typeof value === 'string') {
    return validateEnumTypeForString(value, type);
  }

  switch (type) {
    case 'boolean':
      return typeof value === 'boolean' && (value === true || value === false);
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value);
    case 'number':
      return typeof value === 'number';
    default:
      return false;
  }
}

// Type conversions
function convertBooleanToType(value: boolean, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'string':
      return value.toString();
    case 'integer':
    case 'number':
    case 'boolean':
    default:
      return value;
  }
}

function convertNumberToType(value: number, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'string':
      return value.toString();
    case 'boolean':
      if (value === 1) return true;
      else if (value === 0) return false;
      return value;
    case 'integer':
    case 'number':
    default:
      return value;
  }
}

function convertStringToType(value: string, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'boolean':
      if (value.toLowerCase() === 'true') return true;
      else if (value.toLowerCase() === 'false') return false;
      return value;
    case 'integer': {
      const intNum = Number(value);
      return Number.isNaN(intNum) || !Number.isInteger(intNum) ? value : intNum;
    }
    case 'number': {
      const num = Number(value);
      return Number.isNaN(num) ? value : num;
    }
    case 'string':
    default:
      return value;
  }
}

// Converts a type to a different type if possible
// If not possible, returns the original value with the original type
export function convertToType(value: EnumValue, type: SchemaType | undefined): SupportedTypes {
  // If the value is already the correct type or the type is not supported, return the original value
  const valueType = typeof value;
  if (valueType === type || !type || !isEnumTypeSupported(type)) {
    return value;
  }

  switch (typeof value) {
    case 'boolean':
      return convertBooleanToType(value, type);
    case 'number':
      return convertNumberToType(value, type);
    case 'string':
      return convertStringToType(value, type);
    default:
      return value;
  }
}

export function cleanInputValue(value: string) {
  return value.trim();
}
